package com.snakesworld.game.audio

/**
 * Kotlin/JS Web Audio API SoundManager (procedural tones).
 */
class JsSoundManager : SoundManager {
    private var volume: Float = 0.5f
    private var isMuted: Boolean = false
    private var isBoosting: Boolean = false

    override fun playPelletChime() {
        if (!canPlay()) return
        try {
            js(
                """
                (function() {
                    if (typeof window === 'undefined') return;
                    var AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!window.__swAudioCtx) window.__swAudioCtx = new AudioCtx();
                    var ctx = window.__swAudioCtx;
                    if (ctx.state === 'suspended') ctx.resume();
                    var now = ctx.currentTime;
                    var vol = window.__swVolume != null ? window.__swVolume : 0.5;
                    var osc = ctx.createOscillator();
                    var gain = ctx.createGain();
                    osc.type = 'square';
                    osc.frequency.setValueAtTime(800, now);
                    osc.frequency.exponentialRampToValueAtTime(1700, now + 0.055);
                    gain.gain.setValueAtTime(0.22 * vol, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.055);
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.start(now); osc.stop(now + 0.055);
                })();
                """
            )
        } catch (_: Throwable) {}
    }

    override fun startBoost() {
        if (isBoosting || !canPlay()) return
        isBoosting = true
        try {
            js(
                """
                (function() {
                    if (typeof window === 'undefined') return;
                    var AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!window.__swAudioCtx) window.__swAudioCtx = new AudioCtx();
                    var ctx = window.__swAudioCtx;
                    if (ctx.state === 'suspended') ctx.resume();
                    var now = ctx.currentTime;
                    var vol = window.__swVolume != null ? window.__swVolume : 0.5;
                    var osc = ctx.createOscillator();
                    var gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(115, now);
                    gain.gain.setValueAtTime(0.12 * vol, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.start(now); osc.stop(now + 0.12);
                })();
                """
            )
        } catch (_: Throwable) {}
    }

    override fun stopBoost() {
        isBoosting = false
    }

    override fun playDemise() {
        if (!canPlay()) return
        try {
            js(
                """
                (function() {
                    if (typeof window === 'undefined') return;
                    var AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!window.__swAudioCtx) window.__swAudioCtx = new AudioCtx();
                    var ctx = window.__swAudioCtx;
                    if (ctx.state === 'suspended') ctx.resume();
                    var now = ctx.currentTime;
                    var vol = window.__swVolume != null ? window.__swVolume : 0.5;
                    var osc = ctx.createOscillator();
                    var gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(190, now);
                    osc.frequency.exponentialRampToValueAtTime(40, now + 0.45);
                    gain.gain.setValueAtTime(0.35 * vol, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.start(now); osc.stop(now + 0.45);
                })();
                """
            )
        } catch (_: Throwable) {}
    }

    override fun playStartFanfare() {
        if (!canPlay()) return
        try {
            js(
                """
                (function() {
                    if (typeof window === 'undefined') return;
                    var AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!window.__swAudioCtx) window.__swAudioCtx = new AudioCtx();
                    var ctx = window.__swAudioCtx;
                    if (ctx.state === 'suspended') ctx.resume();
                    var now = ctx.currentTime;
                    var vol = window.__swVolume != null ? window.__swVolume : 0.5;
                    var notes = [261.63, 329.63, 392.00, 523.25];
                    notes.forEach(function(freq, idx) {
                        var start = now + idx * 0.07;
                        var osc = ctx.createOscillator();
                        var gain = ctx.createGain();
                        osc.type = 'square';
                        osc.frequency.value = freq;
                        gain.gain.setValueAtTime(0.2 * vol, start);
                        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.08);
                        osc.connect(gain); gain.connect(ctx.destination);
                        osc.start(start); osc.stop(start + 0.08);
                    });
                })();
                """
            )
        } catch (_: Throwable) {}
    }

    override fun playDirectionClick() {
        if (!canPlay()) return
        try {
            js(
                """
                (function() {
                    if (typeof window === 'undefined') return;
                    var AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!window.__swAudioCtx) window.__swAudioCtx = new AudioCtx();
                    var ctx = window.__swAudioCtx;
                    if (ctx.state === 'suspended') ctx.resume();
                    var now = ctx.currentTime;
                    var vol = window.__swVolume != null ? window.__swVolume : 0.5;
                    var osc = ctx.createOscillator();
                    var gain = ctx.createGain();
                    osc.type = 'square';
                    osc.frequency.setValueAtTime(540, now);
                    osc.frequency.exponentialRampToValueAtTime(340, now + 0.028);
                    gain.gain.setValueAtTime(0.18 * vol, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.028);
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.start(now); osc.stop(now + 0.028);
                })();
                """
            )
        } catch (_: Throwable) {}
    }

    override fun setVolume(volume: Float) {
        this.volume = volume.coerceIn(0f, 1f)
        syncJsGain()
    }

    override fun getVolume(): Float = volume

    override fun setMuted(muted: Boolean) {
        this.isMuted = muted
        syncJsGain()
    }

    private fun canPlay(): Boolean = !isMuted && volume > 0.01f

    private fun syncJsGain() {
        val v = if (isMuted) 0.0 else volume.toDouble()
        try {
            setJsVolume(v)
        } catch (_: Throwable) {}
    }

    private fun setJsVolume(v: Double) {
        js("window.__swVolume = v")
    }
}

actual fun createPlatformSoundManager(): SoundManager = JsSoundManager()
